import type { useSignIn, useSignUp } from "@clerk/react";

import {
  emailLooksComplete,
  hasEnterpriseSsoFactor,
  isEmployeeEmail,
  isEnterpriseSsoError,
} from "@/lib/auth-helpers";
import { clerkFailureMessage } from "@/lib/clerk-errors";
import {
  clerkUsernameFromEmail,
  emailAddressFromClerkTicket,
} from "@/lib/clerk-username";
import { followDecorated } from "@/lib/follow-auth";

type SignInClient = ReturnType<typeof useSignIn>["signIn"];
type SignUpClient = ReturnType<typeof useSignUp>["signUp"];

const INCOMPLETE =
  "Additional verification is required. Contact Anomalos support.";

const finalize = async (client: SignInClient | SignUpClient): Promise<void> => {
  if (client.status !== "complete") {
    throw new Error(INCOMPLETE);
  }
  const { error } = await client.finalize({
    navigate: ({ decorateUrl }) => followDecorated(decorateUrl),
  });
  if (error) {
    throw error;
  }
};

export const redeemClerkTicket = async (
  signIn: SignInClient,
  signUp: SignUpClient,
  token: string,
  status: "complete" | "sign_in" | "sign_up"
): Promise<void> => {
  if (status === "sign_up") {
    const ticketEmail =
      emailAddressFromClerkTicket(token) ?? "user@anomalos.com";
    const { error } = await signUp.create({
      strategy: "ticket",
      ticket: token,
      username: clerkUsernameFromEmail(ticketEmail),
    });
    if (error) {
      throw error;
    }
    await finalize(signUp);
    return;
  }
  const { error } = await signIn.ticket({ ticket: token });
  if (error) {
    throw error;
  }
  await finalize(signIn);
};

export type EmailContinue =
  | { kind: "password" }
  | { kind: "sso" }
  | { kind: "unknown" }
  | { kind: "error"; message: string };

const startSso = async (
  signIn: SignInClient,
  identifier: string
): Promise<EmailContinue> => {
  const { error } = await signIn.sso({
    identifier,
    redirectCallbackUrl: "/sso-callback",
    redirectUrl: "/sso-callback",
    strategy: "enterprise_sso",
  });
  if (error) {
    return { kind: "error", message: clerkFailureMessage(error) ?? "" };
  }
  return { kind: "sso" };
};

export const continueFromEmail = async (
  signIn: SignInClient,
  identifier: string,
  clerkSso: boolean
): Promise<EmailContinue> => {
  if (!emailLooksComplete(identifier)) {
    return { kind: "error", message: "Enter a valid email address." };
  }
  if (isEmployeeEmail(identifier) || clerkSso) {
    return startSso(signIn, identifier);
  }
  const ready =
    signIn.identifier === identifier && signIn.status === "needs_first_factor";
  if (!ready) {
    const { error } = await signIn.create({ identifier });
    if (error?.code === "form_identifier_not_found") {
      return { kind: "unknown" };
    }
    if (error && isEnterpriseSsoError(error.longMessage ?? error.message)) {
      return startSso(signIn, identifier);
    }
    if (error) {
      return { kind: "error", message: clerkFailureMessage(error) ?? "" };
    }
  }
  if (hasEnterpriseSsoFactor(signIn.supportedFirstFactors)) {
    return startSso(signIn, identifier);
  }
  return { kind: "password" };
};
